/**
 * Structured presentation props → CSS. The backend's Presentation lane ships
 * style definitions as resolved JSON (RunProps / ParagraphProps / table
 * regions — theme fonts and colors already concrete), and this module turns
 * them into declarations for the injected Studio stylesheet. The sibling
 * ooxmlCss.js does the same job for the verbatim XML fragments riding on
 * nodes; keep the two mappings consistent.
 */

import { fontFamilyCss } from './fontStack.js'
import { fontMetricsCss, lineSpacingCss, vertAlignCss } from './lineMetrics.js'
import { AUTO_COLOR_CSS } from './ooxmlCss.js'

const BORDER_STYLES = {
  single: 'solid',
  thick: 'solid',
  double: 'double',
  dotted: 'dotted',
  dashed: 'dashed',
  dotDash: 'dashed',
  dotDotDash: 'dashed',
  wave: 'solid',
  none: 'none',
  nil: 'none',
}

const UNDERLINE_STYLES = {
  double: 'double',
  dotted: 'dotted',
  dash: 'dashed',
  wave: 'wavy',
}

const twToPt = (tw) => Number(tw) / 20

const isHex = (value) => typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)

/** {val, szEighthPt, color} → CSS border value ('none' for nil/none). */
export function borderPropCss(border) {
  if (!border) return null
  const style = BORDER_STYLES[border.val] || 'solid'
  if (style === 'none') return 'none'
  const widthPt = Math.max((Number(border.szEighthPt) || 4) / 8, 0.5)
  // Word's border "auto" is black — never the inherited text color (which
  // can be white inside styled table cells; see ooxmlCss borderCss).
  const color = isHex(border.color) ? border.color : 'rgb(0, 0, 0)'
  return `${widthPt}pt ${style} ${color}`
}

/** RunProps JSON → CSS declarations. */
export function runPropsToCss(props) {
  if (!props) return ''
  const css = []

  const ascii = props.fonts?.ascii
  if (ascii) {
    const stack = fontFamilyCss(ascii)
    if (stack) css.push(`font-family: ${stack}`, fontMetricsCss(ascii))
  }
  const script = vertAlignCss(props.vertAlign, props.szPt)
  if (script) css.push(script)
  else if (props.szPt > 0) css.push(`font-size: ${props.szPt}pt`)
  if (props.bold !== undefined) css.push(`font-weight: ${props.bold ? 'bold' : 'normal'}`)
  if (props.italic !== undefined) css.push(`font-style: ${props.italic ? 'italic' : 'normal'}`)
  // A style's explicit "auto" is Word's automatic colour: white on a dark fill.
  if (isHex(props.color)) css.push(`color: ${props.color}`)
  else if (props.color === 'auto') css.push(AUTO_COLOR_CSS)
  if (props.caps) css.push('text-transform: uppercase')
  if (props.smallCaps) css.push('font-variant: small-caps')
  if (props.strike) css.push('text-decoration: line-through')
  if (props.underline) {
    const style = UNDERLINE_STYLES[props.underline.val]
    const color = isHex(props.underline.color) ? ` ${props.underline.color}` : ''
    css.push(`text-decoration: underline${style ? ' ' + style : ''}${color}`)
  }
  if (props.spacingTw) css.push(`letter-spacing: ${twToPt(props.spacingTw)}pt`)
  if (isHex(props.shd?.fill)) css.push(`background-color: ${props.shd.fill}`)

  return css.join('; ')
}

/** ParagraphProps JSON → CSS declarations. */
export function paragraphPropsToCss(props) {
  if (!props) return ''
  const css = []

  const jc = {
    start: 'left',
    left: 'left',
    center: 'center',
    end: 'right',
    right: 'right',
    both: 'justify',
    distribute: 'justify',
  }[props.jc]
  if (jc) css.push(`text-align: ${jc}`)

  const ind = props.ind
  if (ind) {
    if (ind.leftTw > 0) css.push(`margin-left: ${twToPt(ind.leftTw)}pt`)
    if (ind.rightTw > 0) css.push(`margin-right: ${twToPt(ind.rightTw)}pt`)
    if (ind.hangingTw > 0) css.push(`text-indent: -${twToPt(ind.hangingTw)}pt`)
    else if (ind.firstLineTw > 0) css.push(`text-indent: ${twToPt(ind.firstLineTw)}pt`)
  }

  const spacing = props.spacing
  if (spacing) {
    if (spacing.beforeTw !== undefined) css.push(`margin-top: ${twToPt(spacing.beforeTw)}pt`)
    if (spacing.afterTw !== undefined) css.push(`margin-bottom: ${twToPt(spacing.afterTw)}pt`)
    // Word's line model (lineMetrics.js): studio.css derives the height and baseline.
    const lineCss = lineSpacingCss(spacing.line, spacing.lineRule)
    if (lineCss) css.push(lineCss)
  }

  if (props.pBdr) {
    for (const edge of ['top', 'left', 'bottom', 'right']) {
      const border = borderPropCss(props.pBdr[edge])
      if (!border || border === 'none') continue
      css.push(`border-${edge}: ${border}`)
      // w:space on borders is ST_PointMeasure — already points, no /20.
      const space = Number(props.pBdr[edge].spacePt ?? 0)
      if (space > 0) css.push(`padding-${edge}: ${space}pt`)
    }
  }

  if (isHex(props.shd?.fill)) css.push(`background-color: ${props.shd.fill}`)

  // Style-defined tab stops ride the same --studio-tabs custom property the
  // tab layout engine reads (format mirrors pPrToCss's serialization). Inline
  // pPr tabs OVERRIDE style tabs via natural CSS specificity — Word merges
  // the two lists instead; accepted v1 divergence.
  if (Array.isArray(props.tabs)) {
    const stops = props.tabs
      .filter((stop) => stop && stop.val !== 'clear' && Number.isFinite(Number(stop.posTw)))
      .map((stop) => `${twToPt(stop.posTw)} ${stop.val || 'left'} ${stop.leader || 'none'}`)
    if (stops.length) css.push(`--studio-tabs: ${stops.join(', ')}`)
  }

  return css.join('; ')
}
